import { apiService } from './apiService';

export const shipmentServiceReal = {
  // Get all shipments
  async getShipments(params?: { 
    status?: string; 
    driverId?: string;
    assignmentResponseStatus?: string;
    customerId?: string; 
    startDate?: string; 
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.driverId) queryParams.append('driverId', params.driverId);
    if (params?.assignmentResponseStatus) queryParams.append('assignmentResponseStatus', params.assignmentResponseStatus);
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const query = queryParams.toString();
    return await apiService.get(`/shipments${query ? `?${query}` : ''}`);
  },

  // Get shipment by ID
  async getShipment(id: string | number) {
    return await apiService.get(`/shipments/${id}`);
  },

  // Create shipment
  async createShipment(data: any) {
    return await apiService.post('/shipments', data);
  },

  // Update shipment
  async updateShipment(id: string | number, data: any) {
    return await apiService.put(`/shipments/${id}`, data);
  },

  // Delete shipment
  async deleteShipment(id: string | number) {
    return await apiService.delete(`/shipments/${id}`);
  },

  // Change route for active shipment
  async changeRoute(id: string | number, data: {
    reason: string;
    newPickupCity?: string;
    newPickupDistrict?: string;
    newPickupAddress?: string;
    newPickupLatitude?: number | null;
    newPickupLongitude?: number | null;
    newDeliveryCity?: string;
    newDeliveryDistrict?: string;
    newDeliveryAddress?: string;
    newDeliveryLatitude?: number | null;
    newDeliveryLongitude?: number | null;
    newDistanceKm?: number;
    routePoints?: Array<{
      id?: number;
      customer_id?: number | null;
      location_name?: string;
      address?: string;
      city?: string;
      district?: string;
      latitude?: number | null;
      longitude?: number | null;
      stop_order?: number;
      operation_type: 'loading' | 'unloading';
    }>;
  }) {
    return await apiService.post(`/shipments/${id}/change-route`, data);
  },

  // Get shipment route points
  async getRoutes(id: string | number) {
    return await apiService.get(`/shipments/${id}/routes`);
  },

  // Reorder route points
  async reorderRoutes(id: string | number, routeOrder: Array<{
    id: number;
    stop_order: number;
    operation_type: string;
    location_name?: string;
    address?: string;
    city?: string;
    district?: string;
    latitude?: number | null;
    longitude?: number | null;
    customer_id?: number | null;
  }>) {
    return await apiService.put(`/shipments/${id}/routes`, { routeOrder });
  },

  // Add route point
  async addRoutePoint(id: string | number, data: {
    customer_id?: number;
    location_name?: string;
    address?: string;
    city?: string;
    district?: string;
    latitude?: number | null;
    longitude?: number | null;
    operation_type: 'loading' | 'unloading';
    insert_after_order?: number;
  }) {
    return await apiService.post(`/shipments/${id}/routes`, data);
  },

  // Delete route point
  async deleteRoutePoint(id: string | number, routeId: number) {
    return await apiService.delete(`/shipments/${id}/routes/${routeId}`);
  },

  // Get shipment tracking
  async getTracking(shipmentId: string | number) {
    return await apiService.get(`/tracking/shipment/${shipmentId}`);
  },

  async getShipmentTimeline(shipmentId: string | number) {
    return await apiService.get(`/tracking/shipment/${shipmentId}/timeline`);
  },

  async getDriverActiveShipmentTimeline(driverId: string | number) {
    return await apiService.get(`/tracking/driver/${driverId}/active-shipment-timeline`);
  },

  // Update shipment location
  async updateLocation(data: {
    shipmentId?: number;
    latitude: number;
    longitude: number;
    speed?: number;
    heading?: number;
    accuracy?: number;
  }) {
    return await apiService.post('/tracking/update-location', data);
  },

  async getDriverLocation(driverId: string | number) {
    return await apiService.get(`/tracking/driver/${driverId}/location`);
  },

  // Get active shipments for driver
  async getDriverShipments(driverId: string | number) {
    return await apiService.get(`/shipments/driver/${driverId}`);
  },

  async getPendingAssignments(driverId: string | number) {
    return await apiService.get(`/shipments/driver/${driverId}/pending-assignments`);
  },

  async respondAssignment(id: string | number, data: { action: 'accepted' | 'rejected'; note?: string }) {
    return await apiService.post(`/shipments/${id}/respond-assignment`, data);
  },

  // Get supply records
  async getSupplyRecords(params?: { shipmentId?: string; startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.shipmentId) queryParams.append('shipmentId', params.shipmentId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const query = queryParams.toString();
    return await apiService.get(`/supply-records${query ? `?${query}` : ''}`);
  },

  // Create supply record
  async createSupplyRecord(data: any) {
    return await apiService.post('/supply-records', data);
  },

  // Update supply record
  async updateSupplyRecord(id: string | number, data: any) {
    return await apiService.put(`/supply-records/${id}`, data);
  },

  // Delete supply record
  async deleteSupplyRecord(id: string | number) {
    return await apiService.delete(`/supply-records/${id}`);
  },
};
